import React, {ReactNode} from "react";
import {ChartBuilder} from "./ChartBuilder";
import {TFunction} from "i18next";
import {services} from "../../application/service/services";
import {TooltipItem} from "chart.js";
import {TableData, TableItem} from "../../report/view/visualizations/table/AbstractTableViz";
import {VisualizationInstance} from "../../report/model/viz/VisualizationInstance";


export interface DataSet {
    label?: string,
    backgroundColor: string[],
    data: any[],
    hidden?: boolean,
    parsing?: { [x: string]: string; }
}

export interface ChartData {
    labels: string[],
    datasets: DataSet[]
}


export abstract class TableDataChartBuilder extends ChartBuilder {

    protected otherKey: string;
    protected data: TableData;

    protected hiddenItems: Set<string> = new Set<string>();

    protected datasetIndexByGroup = {};

    protected otherIndex: number[] = [];

    protected legendOnClickHandler = (event, legendItem, legend) => {
        //if (this.otherIndex.indexOf(legendItem.datasetIndex) >= 0) return;
        const ci = legend.chart;

        if (!legendItem.hidden) {
            this.hiddenItems.add(legendItem.text);
        } else {
            this.hiddenItems.delete(legendItem.text);
        }

        ci.data = this.computeDatasetsAndLabels()
        // ci.setDatasetVisibility(legendItem.datasetIndex, legendItem.hidden);
        ci.update();
    }


    protected readonly t: TFunction;
    protected viz: VisualizationInstance;
    protected percentageMode: boolean;
    protected stacked: any;
    protected parsingKey: string;
    protected displayVerticalGrid: boolean;
    protected otherGroupPresent: Boolean = false;
    protected formattedData: { datasets: DataSet[]; labels: string[] };

    constructor(type: any, viz: any, t: TFunction, otherkey: string) {
        super(type);
        this.otherKey = otherkey;
        this.t = t;
        this.viz = viz;
        this.percentageMode = this.viz.definition?.uiParams?.percentageMode || false;
        this.stacked = this.viz.definition?.uiParams?.stacked || false;
    }

    _tooltipRenderer = (value, tooltipItem: TooltipItem<any>) => {
        const tdStyle = {borderTop: '4px solid  rgba(0,0,0,0.0)', borderLeft: '14px solid  rgba(0,0,0,0.0)'};
        return <table style={{textAlign: 'left', margin: '0 auto', borderCollapse: 'collapse', borderStyle: 'hidden'}}>
            <tbody>
            <tr>
                <td style={tdStyle}><strong>{this.t('dashboard.viz.distributionChart.tooltip.count')}</strong></td>
                <td style={tdStyle}>{value.value}</td>
            </tr>
            <tr>
                <td style={tdStyle}>{value.label}</td>
            </tr>
            </tbody>
        </table>;
    };

    public withData(data: TableData) {
        this.data = data;
        return this;
    }

    build() {
        const chartObject = this.prepareChartObject(super.build());

        this.prepareFormattedData();

        return {...chartObject, data: this.formattedData};
    }

    protected prepareFormattedData() {
        this.hiddenItems = new Set();
        this.formattedData = this.computeDatasetsAndLabels();
    }

    protected abstract prepareChartObject(chartObject): any;

    protected getVolume(item: TableItem): { count: number, percent: number } {
        return item.data.find(
            x => x.name === "volume"
        )?.value;
    }

    protected addSegment(item: Partial<TableItem>, label: string, columnIndex: number, visible: boolean, value: {
        count: number,
        percent: number
    } | null, color: string, datasets: Map<string, DataSet>, labels: string[]) {
        const key = item.fieldValue; // Use the field value as the key
        if (!datasets.has(key)) {
            datasets.set(key, {
                label,
                data: new Array(labels.length).fill({x: 0, y: 0, value: null, percentage: null}),
                backgroundColor: [color],
                parsing: {[this.parsingKey]: this.percentageMode ? 'percentage' : 'value'},
                hidden: !visible
            });
        }

        datasets.get(key)!.data[columnIndex] = {
            item: item,
            label,
            value: value?.count,
            percentage: value?.percent,
            x: 0,
            y: 0
        };
    }

    /**
     * Construit les segments d'une colonne affichée
     */
    protected buildColumn(item: TableItem, columnIndex: number, datasets: Map<string, DataSet>, labels: string[]) {

        const leaves = this.collectLeaves([item]);
        let displayedTotal = 0;

        for (const leaf of leaves) {
            const label = this.getFieldValueLabel(leaf);
            const visible = !this.hiddenItems.has(label);
            const value = visible ? this.getVolume(leaf) : null;
            const color = services.getColorService().getColorForFieldValue(this.viz.definition.spec.params.field, leaf.fieldValue);

            this.addSegment(leaf, label, columnIndex, visible, value, color, datasets, labels);
            if (visible) {
                displayedTotal += this.getVolume(leaf).count;
            }
        }

        const other = leaves.length === 1 && leaves[0] === item ? 0 : item.childrenCount - displayedTotal;

        if (other > 0) {
            const key = `other`;

            const fieldValue = this.getFieldValueLabel(item);
            const label = this.getOtherGroupName();
            this.otherIndex.push(datasets.size);
            this.addSegment({
                fieldValue: "other",
                field: this.viz.definition.spec.params.field
            }, label, columnIndex, !this.hiddenItems.has(label), {
                count: other,
                percent: 0,

            }, services.getColorService().getOtherColor(), datasets, labels);
        }
    }

    protected collectLeaves(items: TableItem[], result: TableItem[] = []): TableItem[] {
        for (const item of items) {
            if (!item.children || item.children.length === 0) {
                result.push(item);
            } else {
                this.collectLeaves(item.children, result);
            }
        }

        return result;
    }

    /**
     * Retourne toutes les feuilles affichées sous un noeud
     */
    protected getDisplayedLeaves(item: TableItem, hiddenItems: Set<string>): TableItem[] {
        const result: TableItem[] = [];
        this.getDisplayedLeavesRecursive(item, hiddenItems, result);
        return result;
    }

    private getDisplayedLeavesRecursive(item: TableItem, hiddenItems: Set<string>, result: TableItem[]) {
        if (!item.children || item.children.length === 0) {
            const label = this.getFieldValueLabel(item);
            if (!hiddenItems.has(label)) {
                result.push(item);
            }

            return;
        }
        item.children?.forEach(child => this.getDisplayedLeavesRecursive(child, hiddenItems, result));
    }

    protected buildGlobalOtherColumn(root: TableData, displayedColumns: TableItem[], columnIndex: number, datasets: Map<string, DataSet>, labels: string[]) {
        const displayedSet = new Set(displayedColumns);
        let other = 0;
        root.items.forEach(item => {
            if (!displayedSet.has(item)) {
                other += this.getVolume(item).count;
            }
            const leaves = this.collectLeaves([item], []);
            leaves.forEach(hiddenLeaf => {
                const hiddenLeafLabel = this.getFieldValueLabel(hiddenLeaf);
                const color = services.getColorService().getColorForFieldValue(this.viz.definition.spec.params.field, hiddenLeaf.fieldValue);
                this.addSegment(hiddenLeaf, hiddenLeafLabel, columnIndex, false, null, color, datasets, labels);
            })
        });

        //add all segments of invisible column with hidden true to display them in the legend

        if (other > 0) {
            this.otherIndex.push(datasets.size);
            const label = this.getOtherGroupName();
            this.addSegment({
                fieldValue: "other",
                field: this.viz.definition.spec.params.field
            }, label, columnIndex, !this.hiddenItems.has(label), {
                count: other,
                percent: 0,
            }, services.getColorService().getOtherColor(), datasets, labels);
        }
    }

    protected getFieldValueLabel(item: TableItem) {
        const fieldValue = services.getFieldsService().findFieldValue(item.field, item.fieldValue);
        return (fieldValue ? fieldValue.getLabel(this.t) : item.fieldValue);
    }

    protected computeDatasetsAndLabels() {
        this.otherIndex = []
        const datasets = new Map<string, DataSet>();
        const hiddenItemsSet = new Set(this.hiddenItems);
        const displayedColumns =
            this.data.items.filter(item =>
                this.getDisplayedLeaves(item, hiddenItemsSet).length > 0
            );

        const labels = displayedColumns.map(item => this.getLabelWithCount(this.getFieldValueLabel(item), this.getVolume(item).count));

        // colonne Other globale
        let globalOther = 0;

        this.data.items.forEach(item => {
            if (!displayedColumns.includes(item)) {
                globalOther += item.totalCount;
            }
        });

        if (globalOther > 0 && !this.hiddenItems.has(this.getOtherGroupName())) {
            labels.push(this.getLabelWithCount(this.getOtherGroupName(), globalOther));
        }

        displayedColumns.forEach((item, index) => {
            this.buildColumn(item, index, datasets, labels);
        });

        if (globalOther > 0) {
            this.buildGlobalOtherColumn(this.data, displayedColumns, labels.length - 1, datasets, labels);
        }

        return {
            labels,
            datasets: Array.from(datasets.values())
        };


        /* if (!this.hideOtherDataset) {
             this.otherIndex = datasets.length;
             this.datasetIndexByGroup[BarChartBuilder.otherKey] = index;
             this.otherGroupPresent = false;
             if (otherCount !== 0) {
                 this.otherGroupPresent = true;
                 const otherData = {
                     group: this.getOtherGroupName(),
                     value: otherCount,
                     percentage: otherCount * 100 / this.data.totalCount,
                     x: 0,
                     y: 0
                 };
                 const otherDataset = {
                     label: this.getLabelWithCount(this.getOtherGroupName(), otherData),
                     data: voidArray.concat(otherData),
                     backgroundColor: [services.getColorService().getOtherColor()],
                     parsing: {[this.parsingKey]: this.percentageMode ? 'percentage' : 'value'}
                 };
                 datasets.push(otherDataset);
                 labels.push(this.getOtherGroupName());
             }
         }
         return {datasets: Array.from(datasets.values()), labels: labels};*/
    }

    protected getLabelWithCount(label: string, value: any) {
        return label + " (" + value + ")";
    }

    tooltipRenderer(tooltipItem: TooltipItem<any>): (value: any, tooltipItem: TooltipItem<any>) => ReactNode {
        return this._tooltipRenderer;
    }

    protected getOtherGroupName(): string {
        return this.t(this.otherKey);
    }

    public getDataToExport(): any[][] {
        return null;
    }
}